import { scopeFingerprintForAutoDraft } from '../utils/auto-pipeline';

const WAIVER_RECEIPT_FIELDS = [
    'hbcfWaived',
    'hbcfIncludedInScope',
    'hbcfWaiverReason',
    'hbcfWaiverConfirmedBy',
    'hbcfWaiverConfirmedAt',
];

function receiptError() {
    const error = new Error(
        'HBCF was not changed because the server did not return the exact updated scope, quote and waiver.',
    );
    error.code = 'HBCF_CANONICAL_RECEIPT_MISMATCH';
    return error;
}

function waiverReceiptMatches(canonicalFormData, proposedFormData) {
    return !!canonicalFormData && !!proposedFormData
        && WAIVER_RECEIPT_FIELDS.every((field) => (
            Object.is(canonicalFormData[field], proposedFormData[field])
        ));
}

function stableValue(value) {
    if (Array.isArray(value)) return value.map(stableValue);
    if (!value || typeof value !== 'object') return value;
    return Object.keys(value).sort().reduce((out, key) => {
        if (typeof value[key] !== 'undefined') out[key] = stableValue(value[key]);
        return out;
    }, {});
}

function quoteBindingMatches(canonicalQuoteData, proposedQuoteData, proposedScope) {
    if (!proposedQuoteData) return true;
    if (!canonicalQuoteData) return false;
    const expectedScopeFingerprint = scopeFingerprintForAutoDraft(proposedScope);
    if (scopeFingerprintForAutoDraft(proposedQuoteData.scope) !== expectedScopeFingerprint
        || scopeFingerprintForAutoDraft(canonicalQuoteData.scope) !== expectedScopeFingerprint) return false;
    const proposedCompletion = proposedQuoteData?.meta?.completion;
    const canonicalCompletion = canonicalQuoteData?.meta?.completion;
    // A quote with no completion binding (a QuoteMaker quote) is bound by its rows alone.
    if (!proposedCompletion) return !canonicalCompletion;
    if (!canonicalCompletion
        || proposedCompletion.sourceScopeFingerprint !== expectedScopeFingerprint
        || canonicalCompletion.sourceScopeFingerprint !== expectedScopeFingerprint) return false;
    return JSON.stringify(stableValue(canonicalCompletion))
        === JSON.stringify(stableValue(proposedCompletion));
}

/**
 * JavaScript runs this compare-and-set without an await boundary, so callers
 * that were both waiting on the prior save queue cannot replace one another.
 */
export function claimHbcfDecisionSaveOwner(ownerRef, owner) {
    if (!ownerRef || ownerRef.current !== null) {
        const error = new Error('Another HBCF decision is already being saved. Wait for it to finish.');
        error.code = 'PROJECT_COMMERCIAL_DECISION_IN_FLIGHT';
        throw error;
    }
    ownerRef.current = owner;
    return owner;
}

/**
 * Verify that the canonical server row is the exact commercial transaction
 * proposed by the builder before any of it is painted into React state.
 */
export function assertCanonicalHbcfDecisionReceipt({
    canonicalProject,
    proposedScope,
    proposedFormData,
    proposedQuoteData,
} = {}) {
    const scope = Array.isArray(canonicalProject?.scope) ? canonicalProject.scope : null;
    const formData = canonicalProject?.formData || canonicalProject?.form_data || null;
    const quoteData = canonicalProject?.quoteData || canonicalProject?.quote_data || null;
    const scopeMatches = Array.isArray(scope)
        && scopeFingerprintForAutoDraft(scope) === scopeFingerprintForAutoDraft(proposedScope);

    if (!canonicalProject
        || !Array.isArray(proposedScope)
        || !scopeMatches
        || !waiverReceiptMatches(formData, proposedFormData)
        || !quoteBindingMatches(quoteData, proposedQuoteData, proposedScope)) {
        throw receiptError();
    }

    return { scope, formData, quoteData };
}
